import { t } from "i18next";
import HelpButton from "./components/pages/auth-pages/HelpButton";
import AuthHeading from "./components/pages/auth-pages/AuthHeading";
import AuthSubHeading from "./components/pages/auth-pages/AuthSubHeading";
import AuthInput from "./components/pages/auth-pages/AuthInput";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import "../../styles/globals.css";
import { useSEO } from "@/contexts/SeoContext";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { useNewPasswordMutation } from "@/data/user";
import { enqueueSnackbar } from "notistack";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import CaQuestionMark from "./components/icons/CaQuestionMark";

const NewPasswordV3 = () => {
  const { setTitle, setDescription } = useSEO();
  const { mutate, isLoading } = useNewPasswordMutation();
  const { token } = useParams();

  useEffect(() => {
    setTitle("Reset Password");
    setDescription("Enter your new password");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const newPasswordValidationSchema = yup.object().shape({
    password: yup
      .string()
      .matches(/(?=.*?[A-Z])/, t("auth.errors.password_upper"))
      .matches(/(?=.*?[a-z])/, t("auth.errors.password_lower"))
      .matches(/(?=.*?[0-9])/, t("auth.errors.password_number"))
      .matches(
        /(?=.*?[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?])/,
        t("auth.errors.password_special"),
      )
      .min(8, t("auth.errors.password_min"))
      .required(t("auth.errors.password_required")),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password")], t("auth.errors.password_match"))
      .required(t("auth.errors.password_confirm")),
  });

  const onSubmit = (data: { password: string; confirmPassword: string }) => {
    newPasswordValidationSchema
      .validate(data)
      .then(() => {
        mutate({
          password: data.password,
          token: token!,
        });
      })
      .catch((error: Error) =>
        enqueueSnackbar({ message: error.message, variant: "error" }),
      );
  };

  return (
    <TooltipProvider>
      <main className="w-full h-full bg-login-bg flex items-center justify-center md:p-[40px] lg:p-[58px] bg-cover">
        <div className="w-full h-full flex flex-col">
          <div className="flex flex-row justify-end">
            <HelpButton />
          </div>
          <div className="flex h-full flex-col w-full justify-end xl:justify-center items-center">
            <div className="flex flex-col items-center justify-center gap-10">
              <div className="flex flex-col items-center justify-center gap-5">
                <AuthHeading
                  text={t("v3.auth.reset_password_header", {
                    defaultValue: "Change your password 🔑",
                  })}
                />
                <AuthSubHeading
                  text={t("v3.auth.password_sub_header", {
                    defaultValue: "Choose a new password to login",
                  })}
                />
              </div>
              <div className="flex flex-col items-center justify-center gap-5">
                <AuthInput
                  placeholder={t("v3.auth.new_password_text", {
                    defaultValue: "Choose a new password",
                  })}
                  type="password"
                  name="new-password"
                  handleChange={setPassword}
                  regex={/^/}
                />
                <AuthInput
                  placeholder={t("v3.auth.password_confirm", {
                    defaultValue: "Confirm the new password",
                  })}
                  type="password"
                  name="confirm-password"
                  handleChange={setConfirmPassword}
                  regex={/^/}
                />
              </div>

              <div className="flex w-full flex-col gap-2 md:mt-[25vh] xl:mt-10">
                <p className="text-center text-white mb-3">
                  {t("v3.auth.password_warning", {
                    defaultValue: "⚠️ It must contain a number and a symbol",
                  })}
                </p>
                <div className="flex gap-2 w-full justify-center items-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button className="flex  gap-2 h-[30px] rounded-[10px] border-2 bg-transparent">
                        <div>
                          {t("v3.auth.symbol_warning")
                            .split(" ")
                            .map((text) => (
                              <p className="text-[10px] leading-[10px] ">
                                {text}
                              </p>
                            ))}
                        </div>
                        <CaQuestionMark />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" align="end">
                      <div className="bg-white p-2 flex flex-col gap-2 w-[200px]">
                        <p>{t("v3.auth.symbol_warning")}</p>
                        <p>
                          {`A!, @, #, $, %, ^, &, *, (, ), _, +, =, -, [, ], {, }, ;, ', :, ", \\, |, ,, ., <, >, /, ?"`}
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  <Button
                    disabled={
                      password == confirmPassword &&
                      password.length > 0 &&
                      confirmPassword.length > 0
                    }
                    isLoading={isLoading}
                    className="flex flex-auto xl:flex-initial w-full text-sm bg-[#74DFAC] text-black rounded-[10px]  xl:w-[200px] font-semibold h-[30px] hover:bg-[#21FC93]"
                    onClick={() => onSubmit({ password, confirmPassword })}
                    type="button"
                  >
                    {t("v3.auth.reset_password", {
                      defaultValue: "Reset password",
                    })}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </TooltipProvider>
  );
};

export default NewPasswordV3;
